import {
  runSolarProductionSimulate,
  assembleProposalFromCanvas,
  serializeCanvasSavePayload,
  buildProductionJson,
  createProposalData,
  designerReducer,
  dispatchDesignerAction,
  DesignerActionTypes,
  pushHistory,
  undoHistory,
  redoHistory,
  applyHistorySnapshot,
  buildHistorySnapshot,
  createInitialDesignHistoryState,
  applySolarProductionResult,
  applyProductionReset,
  createProductionResetState,
  createProductionResetCanvas,
  createProductionRequestGuard,
  transitionAccordion,
  patchFireSetbacks,
  applySetbacks,
  patchSegmentSetbacks
} from '@aerialytic/designer-states/core';

export function createCrmDesignToolAdapter({
  leadId,
  designId = null,
  lead = null,
  newEndpoint,
  notify,
  iframe
}) {
  const canvasAPI = newEndpoint('designer/canvas');
  const proposalAPI = newEndpoint('designer/proposal/');
  const simulateAPI = newEndpoint('designer/solar-production/simulate/');

  return {
    leadId,
    designId,
    lead,
    canvas: {
      getCanvas: (id) => canvasAPI.retrieve(id),
      saveCanvas: (id, data, partial = false) =>
        canvasAPI.update(id, serializeCanvasSavePayload(data), partial),
      listDesigns: (uid) =>
        canvasAPI.list(`?lead=${encodeURIComponent(uid)}`)
    },
    production: {
      simulate: (payload) => simulateAPI.create(payload)
    },
    proposal: {
      retrieve: (id) => proposalAPI.retrieve(id),
      create: (data) => proposalAPI.create(data),
      update: (id, data) => proposalAPI.update(id, data)
    },
    notify,
    iframe,
    runSimulate: (payload, onResult) =>
      runSolarProductionSimulate(
        {
          production: { simulate: (p) => simulateAPI.create(p) },
          onResult
        },
        payload
      ),
    assembleProposal: assembleProposalFromCanvas,
    buildProductionJson,
    createProposalData,
    serializeCanvasSavePayload,
    designerReducer,
    dispatchDesignerAction,
    DesignerActionTypes,
    pushHistory,
    undoHistory,
    redoHistory,
    applyHistorySnapshot,
    buildHistorySnapshot,
    createInitialDesignHistoryState,
    applySolarProductionResult,
    applyProductionReset,
    createProductionResetState,
    createProductionResetCanvas,
    createProductionRequestGuard,
    transitionAccordion,
    patchFireSetbacks,
    applySetbacks,
    patchSegmentSetbacks
  };
}

export function createDesignerStateController({
  getState,
  setStatePatch,
  getHistories,
  setHistories,
  getHistoryState,
  setHistoryState,
  onIframeState
}) {
  const applyPatch = (patch, { saveHistory = true, iframe = true } = {}) => {
    if (!patch || typeof patch !== 'object') return getState();
    if (saveHistory) {
      const snapshot = buildHistorySnapshot(getState(), patch);
      const next = pushHistory(getHistories(), getHistoryState(), snapshot);
      setHistories(next.histories);
      setHistoryState(next.historyState);
    }
    setStatePatch(patch);
    if (iframe && typeof onIframeState === 'function') {
      onIframeState(patch, false);
    }
    return { ...getState(), ...patch };
  };

  const dispatch = (action, options) => {
    const enrichedAction =
      action?.type === DesignerActionTypes.HISTORY_UNDO ||
      action?.type === DesignerActionTypes.HISTORY_REDO
        ? {
            ...action,
            histories: action.histories || getHistories(),
            historyState: action.historyState || getHistoryState(),
          }
        : action;
    const result = dispatchDesignerAction(getState(), enrichedAction);

    if (result && result.snapshot && (action.type === DesignerActionTypes.HISTORY_UNDO || action.type === DesignerActionTypes.HISTORY_REDO)) {
      setHistories(result.histories);
      setHistoryState(result.historyState);
      const patch = {};
      Object.keys(result.snapshot).forEach((key) => {
        patch[key] = typeof result.snapshot[key] === 'string'
          ? JSON.parse(result.snapshot[key])
          : result.snapshot[key];
      });
      return applyPatch(patch, { ...options, saveHistory: false });
    }

    if (result && result.state) {
      const prev = getState();
      const patch = {};
      Object.keys(result.state).forEach((key) => {
        if (result.state[key] !== prev[key]) patch[key] = result.state[key];
      });
      if (Object.keys(patch).length) {
        return applyPatch(patch, options);
      }
      return prev;
    }

    if (result && typeof result === 'object' && !result.state) {
      const prev = getState();
      const patch = {};
      Object.keys(result).forEach((key) => {
        if (key === 'histories' || key === 'historyState' || key === 'snapshot') return;
        if (result[key] !== prev[key]) patch[key] = result[key];
      });
      if (Object.keys(patch).length) {
        return applyPatch(patch, options);
      }
    }

    return getState();
  };

  return {
    applyPatch,
    dispatch,
    DesignerActionTypes,
    applyAccordion: (accordion) =>
      dispatch({ type: DesignerActionTypes.TRANSITION_ACCORDION, accordion }),
    applyMode: (mode) =>
      dispatch({ type: DesignerActionTypes.SET_MODE, mode }),
    applyTool: (tool) =>
      dispatch({ type: DesignerActionTypes.SET_TOOL, tool }),
    applyMoveMode: (moveMode) =>
      dispatch({ type: DesignerActionTypes.SET_MOVE_MODE, moveMode }),
    applySelectedLabel: (selectedLabel) =>
      dispatch({
        type: DesignerActionTypes.SET_SELECTED_LABEL,
        selectedLabel
      }),
    applySelectRoof: (selectedRoof) =>
      dispatch({ type: DesignerActionTypes.SELECT_ROOF, selectedRoof }),
    applyFireSetbacks: (setback, label) =>
      dispatch({
        type: DesignerActionTypes.PATCH_FIRE_SETBACKS,
        setback,
        label
      }),
    applyProductionResult: (result, ctx) =>
      dispatch({
        type: DesignerActionTypes.APPLY_PRODUCTION_RESULT,
        result,
        ctx
      }),
    resetProduction: (extra) =>
      dispatch({ type: DesignerActionTypes.RESET_PRODUCTION, extra }),
    undo: () => dispatch({ type: DesignerActionTypes.HISTORY_UNDO }, { saveHistory: false }),
    redo: () => dispatch({ type: DesignerActionTypes.HISTORY_REDO }, { saveHistory: false })
  };
}

export {
  runSolarProductionSimulate,
  assembleProposalFromCanvas,
  serializeCanvasSavePayload,
  buildProductionJson,
  createProposalData,
  designerReducer,
  dispatchDesignerAction,
  DesignerActionTypes,
  pushHistory,
  undoHistory,
  redoHistory,
  applyHistorySnapshot,
  buildHistorySnapshot,
  applySolarProductionResult,
  applyProductionReset,
  createProductionRequestGuard,
  transitionAccordion
};
